import React, { Fragment, useCallback, useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Alert from "../../layout/Alert";
import ResponsiveContentWrapper from "../../layout/ResponsiveContentWrapper";
import { connect } from "react-redux";
import { addMfaDevice, getMfaDevices, removeMfaDevice, enableMFA, disableMFA, prepareMfaDeviceVerification } from "../../../action/auth";
import { setAlert } from "../../../action/alert";
import ChangePassword from "./changePassword";
import { UserContext } from "../../routing/Routes";
import AppButton from "../../Common/Button/Button";
import IconButton from "../../Common/Button/IconButton";
import SwitchButton from "../../Common/Button/SwitchButton";
import Dialog from "../../Common/Dailog/Dailog";
import FormInput from "../../Common/FormInputs/MuiTextField";
import env from "../../../env";
import customConfig from "../../../config";
import { generateUpdateProfileOtp, validateUpdateProfileOtp } from "../../../action/password";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteIcon from "@mui/icons-material/Delete";

const Settings = ({
  wrapperClass,
  addMfaDevice,
  getMfaDevices,
  removeMfaDevice,
  enableMFA,
  disableMFA,
  prepareMfaDeviceVerification,
  setAlert: showAlert,
  generateUpdateProfileOtp: sendUpdateProfileOtp,
  validateUpdateProfileOtp: confirmUpdateProfileOtp,
}) => {
  const userInfo = useContext(UserContext);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [verifyType, setVerifyType] = useState("");
  const [verifyValue, setVerifyValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpFlowLoading, setOtpFlowLoading] = useState(false);
  const [mfaDevices, setMfaDevices] = useState([]);
  const [mfaDevicesLoading, setMfaDevicesLoading] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [deviceNameError, setDeviceNameError] = useState("");
  const [addDeviceLoading, setAddDeviceLoading] = useState(false);
  const [removeDeviceName, setRemoveDeviceName] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaToggleLoading, setMfaToggleLoading] = useState(false);
  const [openDisableMfaDialog, setOpenDisableMfaDialog] = useState(false);
  const [verifyDeviceLoading, setVerifyDeviceLoading] = useState(false);

  const handleOpenChangePassword = (event) => {
    event?.currentTarget?.blur?.();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleOpenConfirmationDialog = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setDeviceName("");
    setDeviceNameError("");
    setOpenConfirmationDialog(true);
  };

  const handleCloseConfirmationDialog = () => {
    if (addDeviceLoading) return;
    setOpenConfirmationDialog(false);
    setDeviceName("");
    setDeviceNameError("");
  };

  const fetchMfaDevices = useCallback(async () => {
    try {
      setMfaDevicesLoading(true);
      const devices = await getMfaDevices();
      setMfaDevices(Array.isArray(devices) ? devices : []);
    } catch (err) {
      setMfaDevices([]);
    } finally {
      setMfaDevicesLoading(false);
    }
  }, [getMfaDevices]);

  const fetchMfaStatus = useCallback(async () => {
    try {
      const iamURL = env.GET_IAM_API_URL();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Tenantid: env.GET_TENANT_ID(),
        },
      };
      const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, config);
      const data = response?.data || {};
      setMfaEnabled(Boolean(data.mfaEnabled));
      setProfileData((prev) => ({ ...(prev || {}), ...data }));
    } catch (err) {
      // Keep current toggle state; profile errors are shown when user opens Profile
    }
  }, []);

  useEffect(() => {
    fetchMfaDevices();
    fetchMfaStatus();
  }, [fetchMfaDevices, fetchMfaStatus]);

  const handleOpenChangeDeviceDialog = async () => {
    const trimmedName = (deviceName || "").trim();
    if (!trimmedName) {
      setDeviceNameError(customConfig.mfaDeviceNameRequired);
      return;
    }

    const isDuplicate = mfaDevices.some(
      (device) =>
        String(device?.deviceName || "").trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setDeviceNameError(customConfig.mfaDeviceNameDuplicate);
      return;
    }

    try {
      setAddDeviceLoading(true);
      setDeviceNameError("");
      localStorage.setItem("mfaReturnPath", "/settings");
      const result = await addMfaDevice(trimmedName);
      if (result?.qrCodeUrl) {
        setOpenConfirmationDialog(false);
        setDeviceName("");
        setDeviceNameError("");
      }
    } finally {
      setAddDeviceLoading(false);
    }
  };

  const handleConfirmRemoveDevice = async () => {
    if (!removeDeviceName) return;
    try {
      setRemoveLoading(true);
      await removeMfaDevice(removeDeviceName);
      setRemoveDeviceName("");
      await fetchMfaDevices();
    } catch (err) {
      // Alert already shown in action
    } finally {
      setRemoveLoading(false);
    }
  };

  const openMfaVerifyDialog = async (name) => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const trimmedName = (name || "").trim();
    if (!trimmedName || verifyDeviceLoading) return;

    try {
      setVerifyDeviceLoading(true);
      localStorage.setItem("mfaReturnPath", "/settings");
      // Same enrollment UI as Add Device: QR → Continue → sync codes
      await prepareMfaDeviceVerification(trimmedName);
    } finally {
      setVerifyDeviceLoading(false);
    }
  };

  const handleMfaToggle = () => {
    if (mfaToggleLoading) return;
    if (mfaEnabled) {
      setOpenDisableMfaDialog(true);
      return;
    }
    handleEnableMfa();
  };

  const handleEnableMfa = async () => {
    try {
      setMfaToggleLoading(true);
      await enableMFA();
      setMfaEnabled(true);
      setProfileData((prev) => (prev ? { ...prev, mfaEnabled: true } : prev));
    } catch (err) {
      // Alert already shown in action
    } finally {
      setMfaToggleLoading(false);
    }
  };

  const handleConfirmDisableMfa = async () => {
    try {
      setMfaToggleLoading(true);
      await disableMFA();
      setMfaEnabled(false);
      setProfileData((prev) => (prev ? { ...prev, mfaEnabled: false } : prev));
      setOpenDisableMfaDialog(false);
    } catch (err) {
      // Alert already shown in action
    } finally {
      setMfaToggleLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const getContactValue = (value) => {
    return value === null || value === undefined || value === "" ? "Not added" : String(value);
  };

  const renderContactValue = (value, isVerified) => {
    const contact = getContactValue(value);
    const hasValue = value !== null && value !== undefined && value !== "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography variant="body2">{contact}</Typography>
        {hasValue && isVerified && (
          <CheckCircleRoundedIcon
            sx={{ color: "success.main", fontSize: 18 }}
            titleAccess="Verified"
          />
        )}
      </Box>
    );
  };

  const renderRoleChips = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return <Typography variant="body2">-</Typography>;
    }

    return (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {roles.map((role) => (
          <Chip
            key={String(role)}
            label={String(role)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  const extractErrorMessage = (err) => {
    if (!err) return "Something went wrong. Please try again.";
    if (typeof err === "string") return err;
    if (err.response?.data) {
      const d = err.response.data;
      if (typeof d === "string") return d;
      if (d.message) return d.message;
      if (d.error) return d.error;
    }
    return err.message || "Something went wrong. Please try again.";
  };

  const fetchUserProfile = async (force = false) => {
    if (!force && (profileData || profileLoading)) {
      return;
    }
    const silent = Boolean(force && profileData);
    try {
      if (!silent) {
        setProfileLoading(true);
      }
      const iamURL = env.GET_IAM_API_URL();
      const tenantId = env.GET_TENANT_ID();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Tenantid: tenantId,
        },
      };
      const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, config);
      const data = response?.data || {};
      setProfileData(data);
      setMfaEnabled(Boolean(data.mfaEnabled));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch user profile";
      showAlert(msg, "danger");
    } finally {
      if (!silent) {
        setProfileLoading(false);
      }
    }
  };

  const openVerificationPopup = (type, value) => {
    if (!value) {
      showAlert(`No ${type} available to verify`, "danger");
      return;
    }
    setVerifyType(type);
    setVerifyValue(value);
    setOtp("");
    setOtpSent(false);
    setOpenVerifyDialog(true);
  };

  const closeVerificationPopup = () => {
    setOpenVerifyDialog(false);
    setVerifyType("");
    setVerifyValue("");
    setOtpSent(false);
    setOtp("");
  };

  const handleSendOtp = async () => {
    const raw = (verifyValue || "").trim();
    if (!raw) {
      showAlert("Please enter a valid value.", "danger");
      return;
    }
    try {
      setOtpFlowLoading(true);
      const payload =
        verifyType === "mobile" ? { mobile: raw } : { email: raw };
      await sendUpdateProfileOtp(payload);
      setOtpSent(true);
      setOtp("");
      showAlert("OTP sent successfully.", "success");
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const value = (verifyValue || "").trim();
    const code = (otp || "").trim();
    if (!value) {
      showAlert("Please enter a valid value.", "danger");
      return;
    }
    if (!code) {
      showAlert("Please enter the OTP.", "danger");
      return;
    }
    try {
      setOtpFlowLoading(true);
      const payload =
        verifyType === "mobile"
          ? { mobile: value, otp: code }
          : { email: value, otp: code };
      await confirmUpdateProfileOtp(payload);
      showAlert("Verification completed successfully.", "success");
      await fetchUserProfile(true);
      closeVerificationPopup();
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  return (
    <Fragment>
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {userInfo.level > 1 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Need to update your password?
                  </Typography>
                </Box>
                <AppButton variant="primary" size="medium" onClick={handleOpenChangePassword} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
                  Change Password
                </AppButton>
              </Stack>
            </Paper>
          )}
          <ChangePassword open={open} onClose={handleClose} />
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Two Step Authentication
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {mfaEnabled ? "Enabled" : "Disabled"}
                  </Typography>
                  <SwitchButton
                    id="mfa-account-toggle"
                    checked={mfaEnabled}
                    disabled={mfaToggleLoading}
                    tooltip={mfaEnabled ? "Disable MFA for account" : "Enable MFA for account"}
                    onChange={handleMfaToggle}
                  />
                  {mfaToggleLoading && <CircularProgress size={16} />}
                </Stack>
              </Box>
              <AppButton
                variant="primary"
                size="medium"
                onClick={handleOpenConfirmationDialog}
                sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}
              >
                Add Device
              </AppButton>
            </Stack>
            {mfaDevicesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : mfaDevices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No MFA devices registered.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  // Scroll only when more than 3 devices; 3 rows fit fully with no scrollbar
                  maxHeight: mfaDevices.length > 3 ? 184 : "none",
                  overflowY: mfaDevices.length > 3 ? "auto" : "visible",
                  ...(mfaDevices.length > 3
                    ? {
                        "& .MuiTableHead-root .MuiTableCell-root": {
                          py: 1,
                          backgroundColor: "background.paper",
                        },
                        "& .MuiTableBody-root .MuiTableRow-root": {
                          height: 48,
                        },
                        "& .MuiTableBody-root .MuiTableCell-root": {
                          py: 0.75,
                        },
                      }
                    : {}),
                }}
              >
                <Table size="small" stickyHeader={mfaDevices.length > 3}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Device Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mfaDevices.map((device) => (
                      <TableRow key={device.deviceName}>
                        <TableCell>{device.deviceName || "-"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={device.verified ? "Verified" : "Not Verified"}
                              color={device.verified ? "success" : "default"}
                              variant={device.verified ? "filled" : "outlined"}
                            />
                            {!device.verified && (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => openMfaVerifyDialog(device.deviceName)}
                                disabled={verifyDeviceLoading}
                                sx={{ fontSize: 12 }}
                              >
                                {verifyDeviceLoading ? "Loading..." : "Verify"}
                              </Link>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateTime(device.createdAt)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            variant="danger"
                            size="small"
                            icon={<DeleteIcon />}
                            tooltip="Remove device"
                            onClick={() => setRemoveDeviceName(device.deviceName)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                User Information
              </Typography>
              <AppButton variant="primary" size="medium" onClick={fetchUserProfile} sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}>
                {profileLoading ? "Loading..." : "Profile"}
              </AppButton>
            </Stack>
            {profileData && (
              <Box sx={{ mt: 2 }}>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.username)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Mobile</Typography>
                    {renderContactValue(profileData.mobile, profileData.mobileVerified)}
                    {!profileData.mobileVerified && profileData.mobile && (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => openVerificationPopup("mobile", profileData.mobile)}
                        sx={{ fontSize: 12 }}
                      >
                        Verify
                      </Link>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    {renderContactValue(profileData.email, profileData.emailVerified)}
                    {!profileData.emailVerified && profileData.email && (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => openVerificationPopup("email", profileData.email)}
                        sx={{ fontSize: 12 }}
                      >
                        Verify
                      </Link>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">MFA Enabled</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.mfaEnabled)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Last Logged In</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.lastLoggedInTs)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Creation Date</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.creationDate)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Roles</Typography>
                    {renderRoleChips(profileData.roles)}
                  </Grid2>
                </Grid2>
              </Box>
            )}
          </Paper>
          <Alert />
        </Box>
      </ResponsiveContentWrapper>
      <Dialog
        open={openConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        title="Add Two-Step Authentication Device"
        maxWidth="sm"
        closeOnBackdropClick={!addDeviceLoading}
        closeOnEscapeKeyDown={!addDeviceLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: handleCloseConfirmationDialog,
            disabled: addDeviceLoading,
          },
          {
            label: addDeviceLoading ? "Adding..." : "Ok",
            variant: "primary",
            onClick: handleOpenChangeDeviceDialog,
            disabled: addDeviceLoading,
            loading: addDeviceLoading,
          },
        ]}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter a unique device name, then scan the QR code with your authenticator app.
          </Typography>
          <FormInput
            label="Device Name *"
            name="deviceName"
            value={deviceName}
            onChange={(e) => {
              setDeviceName(e.target.value);
              if (deviceNameError) setDeviceNameError("");
            }}
            fullWidth
            size="small"
            error={Boolean(deviceNameError)}
            helperText={deviceNameError || ""}
            touched="true"
            disabled={addDeviceLoading}
          />
        </Box>
      </Dialog>
      <Dialog
        open={Boolean(removeDeviceName)}
        onClose={() => {
          if (!removeLoading) setRemoveDeviceName("");
        }}
        title="Remove MFA Device?"
        maxWidth="sm"
        closeOnBackdropClick={!removeLoading}
        closeOnEscapeKeyDown={!removeLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: () => setRemoveDeviceName(""),
            disabled: removeLoading,
          },
          {
            label: removeLoading ? "Removing..." : "Remove",
            variant: "primary",
            onClick: handleConfirmRemoveDevice,
            disabled: removeLoading,
            loading: removeLoading,
          },
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to remove device &quot;{removeDeviceName}&quot;? This cannot be undone.
        </Typography>
      </Dialog>
      <Dialog
        open={openDisableMfaDialog}
        onClose={() => {
          if (!mfaToggleLoading) setOpenDisableMfaDialog(false);
        }}
        title="Disable Two-Step Authentication?"
        maxWidth="sm"
        closeOnBackdropClick={!mfaToggleLoading}
        closeOnEscapeKeyDown={!mfaToggleLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: () => setOpenDisableMfaDialog(false),
            disabled: mfaToggleLoading,
          },
          {
            label: mfaToggleLoading ? "Disabling..." : "Disable",
            variant: "primary",
            onClick: handleConfirmDisableMfa,
            disabled: mfaToggleLoading,
            loading: mfaToggleLoading,
          },
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to disable Two-Step Authentication for your account? You can enable it again later.
        </Typography>
      </Dialog>
      <Dialog
        open={openVerifyDialog}
        onClose={closeVerificationPopup}
        title={`${verifyType === "mobile" ? "Mobile" : "Email"} Verification`}
        maxWidth="xs"
        fullWidth
        closeOnBackdropClick
        closeOnEscapeKeyDown
        actions={
          !otpSent
            ? [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeVerificationPopup,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Sending..." : "Send OTP",
                  variant: "primary",
                  onClick: handleSendOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
            : [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeVerificationPopup,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Verifying..." : "Verify OTP",
                  variant: "primary",
                  onClick: handleVerifyOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <FormInput
            label={verifyType === "mobile" ? "Mobile Number" : "Email"}
            name="verifyTarget"
            value={verifyValue}
            onChange={(e) => setVerifyValue(e.target.value)}
            fullWidth
            size="small"
          />
          {otpSent && (
            <FormInput
              label="Enter OTP"
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </Box>
      </Dialog>
    </Fragment>
  );
};

Settings.propTypes = {
  addMfaDevice: PropTypes.func.isRequired,
  getMfaDevices: PropTypes.func.isRequired,
  removeMfaDevice: PropTypes.func.isRequired,
  enableMFA: PropTypes.func.isRequired,
  disableMFA: PropTypes.func.isRequired,
  prepareMfaDeviceVerification: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  generateUpdateProfileOtp: PropTypes.func.isRequired,
  validateUpdateProfileOtp: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  addMfaDevice: (deviceName) => dispatch(addMfaDevice(deviceName)),
  getMfaDevices: () => dispatch(getMfaDevices()),
  removeMfaDevice: (deviceName) => dispatch(removeMfaDevice(deviceName)),
  enableMFA: () => dispatch(enableMFA()),
  disableMFA: () => dispatch(disableMFA()),
  prepareMfaDeviceVerification: (deviceName) =>
    dispatch(prepareMfaDeviceVerification(deviceName)),
  setAlert: (msg, type) => dispatch(setAlert(msg, type)),
  generateUpdateProfileOtp: (payload) => dispatch(generateUpdateProfileOtp(payload)),
  validateUpdateProfileOtp: (payload) => dispatch(validateUpdateProfileOtp(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
